/**
 * Axios instance that attaches the Auth0 Bearer token and retries on 401.
 *
 * Usage:
 *   import { authedClient } from 'api/authedClient';
 *   const { data } = await authedClient.get('/api/current-level');
 */

import axios from 'axios';
import { getToken } from './authToken';

const dataServeUrl = process.env.REACT_APP_DATA_SERVE_ENDPOINT;

const authedClient = axios.create({
  baseURL: dataServeUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
});

// ── Request interceptor – attach Bearer token ──────────────────────────

authedClient.interceptors.request.use(async (config) => {
  const token = await getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor – retry once on 401 ──────────────────────────

let _isRefreshing = false;
let _pendingQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (err: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  _pendingQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  _pendingQueue = [];
};

authedClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only retry 401 once per request
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (_isRefreshing) {
      // Queue while another refresh is in-flight
      return new Promise((resolve, reject) => {
        _pendingQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return authedClient(originalRequest);
        })
        .catch(Promise.reject);
    }

    originalRequest._retry = true;
    _isRefreshing = true;

    try {
      const token = await getToken();
      processQueue(null, token);
      if (token) {
        originalRequest.headers.Authorization = `Bearer ${token}`;
      }
      return authedClient(originalRequest);
    } catch (err) {
      processQueue(err, null);
      return Promise.reject(err);
    } finally {
      _isRefreshing = false;
    }
  },
);

export { authedClient };
